import { toast } from 'sonner';
import { type FormEvent, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import type { EncounterOut } from '@/features/emr';

type Billing = {
  patient: Party;
  patient_dob: string;
  provider: Party;
  tax_id: string;
  coverage: {
    payer_name: string;
    member_id: string;
    group_number: string;
    subscriber: Party;
    subscriber_dob: string;
    relationship: string;
    effective_from: string;
    effective_to: string | null;
  };
  encounter: {
    source_system: string;
    encounter_id: string;
    source_version: string;
    signed: boolean;
    patient_id: string;
    policy_id: string;
    rendering_npi: string;
    diagnoses: string[];
    lines: Line[];
  };
};
import { Textarea } from '@/components/ui/textarea';
import { SuperbillActions } from './SuperbillActions';
import { NoPermission, usePermissions } from '@/features/permissions';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type Claim = {
  eob: {
    allowed_cents: number;
    paid_to_patient_cents: number;
    deductible_cents: number;
    payer_claim_number: string;
  } | null;
  id: string;
  encounter_id: string;
  status: string;
  version: number;
  snapshot: Billing;
  source_changed: boolean;
  review: { member_instructions: string; filing_deadline: string | null } | null;
};
type EncounterPage = { data: EncounterOut[]; meta: { has_next_page: boolean; page: number } };
type Line = {
  source_line_id: string;
  service_date: string;
  code: string;
  modifiers: string[];
  diagnosis_pointers: number[];
  place_of_service: string;
  units: number | string;
  charge_cents: number;
  paid_cents: number;
};
type Party = { name: string; address: string; city: string; state: string; zip_code: string };
const dollars = (value: number) =>
  new Intl.NumberFormat('en-US', { currency: 'USD', style: 'currency' }).format(value / 100);
const cents = (value: string) => {
  if (!/^\d+(\.\d{1,2})?$/.test(value)) throw new Error('Enter dollar amounts with no more than two decimal places.');
  const [whole, fraction = ''] = value.split('.');
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
};

export function ActionForm({
  kind,
  busy,
  claim,
  onSave,
}: {
  kind: 'approve' | 'submission' | 'eob';
  claim: Pick<Claim, 'id' | 'version'>;
  busy: boolean;
  onSave: (path: string, body: unknown) => void;
}) {
  const [error, setError] = useState('');
  return (
    <form
      key={`${claim.id}-${kind}`}
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        const data = new FormData(e.currentTarget);
        const v = (key: string) => String(data.get(key) || '');
        try {
          const body =
            kind === 'approve'
              ? {
                  release_on_file: true,
                  services_verified: true,
                  instruction_source: v('source'),
                  member_instructions: v('instructions'),
                  filing_deadline: v('deadline') || null,
                }
              : kind === 'submission'
                ? { method: v('method'), reference: v('reference'), submitted_on: v('submitted_on') }
                : {
                    outcome: v('outcome'),
                    received_on: v('received_on'),
                    allowed_cents: cents(v('allowed')),
                    paid_to_patient_cents: cents(v('paid')),
                    deductible_cents: cents(v('deductible')),
                    payer_claim_number: v('payer_claim_number'),
                  };
          setError('');
          onSave(`/${claim.id}/${kind}`, { ...body, expected_version: claim.version });
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Check amounts.');
        }
      }}>
      <h3 className="font-medium">
        {kind === 'approve'
          ? 'Approve billing and submission instructions'
          : kind === 'submission'
            ? 'Record an external submission'
            : 'Record explanation of benefits'}
      </h3>
      {kind === 'approve' ? (
        <>
          <Label htmlFor="instructions">Verified member submission instructions</Label>
          <Textarea id="instructions" required minLength={10} name="instructions" />
          <Field name="source" label="Source of instructions (plan document or verified URL)" />
          <Field
            type="date"
            name="deadline"
            required={false}
            label="Verified filing deadline (leave blank if unknown)"
          />
          <label className="flex gap-3">
            <input required type="checkbox" />I verified the services and have the patient's release authorization on
            file.
          </label>
        </>
      ) : kind === 'submission' ? (
        <>
          <Choice name="method" value="portal" label="Submission method" values={['portal', 'mail', 'fax']} />
          <Field type="date" name="submitted_on" label="Date submitted" />
          <Field name="reference" label="Confirmation or tracking reference" />
        </>
      ) : (
        <>
          <Field name="payer_claim_number" label="Payer claim number" />
          <Choice value="paid" name="outcome" label="Outcome" values={['paid', 'partial', 'denied', 'deductible']} />
          <Field name="allowed" label="Allowed amount ($)" />
          <Field name="paid" label="Paid to patient ($)" />
          <Field name="deductible" label="Applied to deductible ($)" />
          <Field type="date" name="received_on" label="EOB received" />
        </>
      )}
      {error && <p role="alert">{error}</p>}
      <Button type="submit" disabled={busy}>
        {busy
          ? 'Saving…'
          : kind === 'approve'
            ? 'Approve record'
            : kind === 'submission'
              ? 'Record submission'
              : 'Save reported outcome'}
      </Button>
    </form>
  );
}
export function ClaimsPage() {
  const { organization } = useAuth();
  // A remount clears all form/detail state when switching organizations.
  return <ClaimsWorkspace key={organization?.organization_id} orgId={organization?.organization_id} />;
}
function BillingForm({
  busy,
  billing,
  onSubmit,
  onCancel,
  encounters,
  encounterId,
  setEncounterId,
}: {
  billing?: Billing;
  encounters: EncounterOut[];
  encounterId: string;
  setEncounterId: (s: string) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  busy: boolean;
  onCancel: () => void;
}) {
  const [count, setCount] = useState(billing?.encounter.lines.length || 1);
  const [relationship, setRelationship] = useState(billing?.coverage.relationship || 'self');
  return (
    <Card>
      <CardHeader>
        <CardTitle>{billing ? 'Edit billing record' : 'New billing record'}</CardTitle>
        <CardDescription>Enter verified billing information. Saving a draft does not submit a claim.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="flex flex-col gap-8" onSubmit={onSubmit}>
          {!billing && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="encounter">Encounter</Label>
              <Select required value={encounterId} onValueChange={setEncounterId}>
                <SelectTrigger id="encounter">
                  <SelectValue placeholder="Choose an encounter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {encounters
                      .filter((e) => e.patient_id)
                      .map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.encounter_date} · {e.patient?.name || e.patient?.mrn}
                        </SelectItem>
                      ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}
          <PartyFields title="Patient" prefix="patient" party={billing?.patient} />
          <Field type="date" name="patient_dob" value={billing?.patient_dob} label="Patient date of birth" />
          <PartyFields prefix="provider" party={billing?.provider} title="Rendering provider / practice" />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field name="npi" label="Rendering NPI" value={billing?.encounter.rendering_npi} />
            <Field name="tax_id" value={billing?.tax_id} label="Practice tax ID (9 digits)" />
          </div>
          <fieldset className="grid gap-4 sm:grid-cols-2">
            <legend className="mb-4 font-medium">Coverage</legend>
            <Field name="payer_name" label="Insurer / plan name" value={billing?.coverage.payer_name} />
            <Field name="member_id" label="Member ID" value={billing?.coverage.member_id} />
            <Field
              required={false}
              name="group_number"
              label="Group number (optional)"
              value={billing?.coverage.group_number}
            />
            <Field type="date" name="effective_from" label="Coverage start" value={billing?.coverage.effective_from} />
            <Field
              type="date"
              required={false}
              name="effective_to"
              label="Coverage end (optional)"
              value={billing?.coverage.effective_to || ''}
            />
            <div className="flex flex-col gap-2">
              <Label htmlFor="relationship">Patient relationship to subscriber</Label>
              <Select name="relationship" value={relationship} onValueChange={setRelationship}>
                <SelectTrigger id="relationship">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {['self', 'spouse', 'child', 'other'].map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </fieldset>
          {relationship !== 'self' && (
            <>
              <PartyFields title="Subscriber" prefix="subscriber" party={billing?.coverage.subscriber} />
              <Field
                type="date"
                name="subscriber_dob"
                label="Subscriber date of birth"
                value={billing?.coverage.subscriber_dob}
              />
            </>
          )}
          <Field
            name="diagnoses"
            value={billing?.encounter.diagnoses.join(', ')}
            label="ICD-10 diagnoses, separated by commas (in pointer order)"
          />
          <input type="hidden" value={count} name="line_count" />
          {Array.from({ length: count }, (_, i) => {
            const l = billing?.encounter.lines[i];
            return (
              <fieldset key={i} className="grid gap-4 sm:grid-cols-3">
                <legend className="mb-4 font-medium">Service {i + 1}</legend>
                <Field value={l?.code} name={`code_${i}`} label="CPT / HCPCS" />
                <Field
                  required={false}
                  name={`modifiers_${i}`}
                  value={l?.modifiers.join(', ')}
                  label="Modifiers (comma-separated)"
                />
                <Field
                  name={`pointers_${i}`}
                  label="Diagnosis positions (e.g. 1,2)"
                  value={l?.diagnosis_pointers.join(',') || '1'}
                />
                <Field name={`pos_${i}`} label="Place of service" value={l?.place_of_service} />
                <Field label="Units" type="number" name={`units_${i}`} value={l?.units || 1} />
                <Field
                  name={`charge_${i}`}
                  label="Line charge ($)"
                  value={l ? (l.charge_cents / 100).toFixed(2) : ''}
                />
                <Field
                  name={`paid_${i}`}
                  label="Patient payment for this line ($)"
                  value={l ? (l.paid_cents / 100).toFixed(2) : '0.00'}
                />
              </fieldset>
            );
          })}
          <div className="flex flex-wrap gap-3">
            <Button type="button" variant="outline" disabled={count >= 50} onClick={() => setCount(count + 1)}>
              Add service
            </Button>
            <Button type="button" variant="outline" disabled={count <= 1} onClick={() => setCount(count - 1)}>
              Remove last service
            </Button>
          </div>
          <label className="flex items-start gap-3">
            <input required type="checkbox" />I have verified these services against the clinical record.
          </label>
          <div className="flex gap-3">
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save draft'}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function Choice({ name, label, value, values }: { name: string; label: string; values: string[]; value: string }) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={name}>{label}</Label>
      <Select required name={name} defaultValue={value}>
        <SelectTrigger id={name}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {values.map((v) => (
              <SelectItem key={v} value={v}>
                {v.replaceAll('_', ' ')}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
}

function ClaimsWorkspace({ orgId }: { orgId?: string }) {
  const { can } = usePermissions();
  const cache = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [newEncounter, setNewEncounter] = useState('');
  const [editSource, setEditSource] = useState<EncounterOut | null>(null);
  const [page, setPage] = useState(0);
  const [error, setError] = useState('');
  const [patientLink, setPatientLink] = useState<{ claimId: string; url: string } | null>(null);
  const base = `/api/v1/organizations/${orgId}/claims`;
  const queryKey = ['claims', orgId, page];
  const claims = useQuery<Claim[]>({
    queryKey,
    enabled: !!orgId && can('read_claim'),
    queryFn: async () => (await clientApi.get(`${base}?limit=50&offset=${page * 50}`)).data,
  });
  // `EncounterPage` is named rather than inferred from `queryFn`. Lint sorts
  // object keys alphabetically, which puts `getNextPageParam` above `queryFn`
  // and leaves TanStack with nothing to infer the page shape from.
  const encounters = useInfiniteQuery<EncounterPage>({
    initialPageParam: 1,
    queryKey: ['claims-encounters', orgId],
    enabled: !!orgId && editing && can('read_encounter'),
    getNextPageParam: (last) => (last.meta.has_next_page ? last.meta.page + 1 : undefined),
    queryFn: async ({ pageParam }) =>
      (await clientApi.get<EncounterPage>(`/api/v1/organizations/${orgId}/encounters?limit=100&page=${pageParam}`))
        .data,
  });
  const encounterOptions = encounters.data?.pages.flatMap((p) => p.data) || [];
  const detail = useQuery<Claim>({
    enabled: !!selectedId && can('read_claim'),
    queryKey: ['claim-detail', orgId, selectedId],
    queryFn: async () => (await clientApi.get(`${base}/${selectedId}`)).data,
  });
  const selected = detail.data || claims.data?.find((c) => c.id === selectedId);
  const history = useQuery<{ action: string; version: number | null; occurred_at: string }[]>({
    enabled: !!selectedId && can('read_claim'),
    queryKey: ['claim-history', orgId, selectedId, selected?.version],
    queryFn: async () => (await clientApi.get(`${base}/${selectedId}/history`)).data,
  });
  const mutation = useMutation({
    mutationFn: async ({ path, body, method = 'post' }: { path: string; body: unknown; method?: 'post' | 'put' }) =>
      (await clientApi[method]<Claim>(`${base}${path}`, body)).data,
    onError: () =>
      setError(
        'Unable to save. Check the billing fields and reload if the encounter or claim changed. No submission has been sent by this action.',
      ),
    onSuccess: (claim) => {
      cache.invalidateQueries({ queryKey: ['claims', orgId] });
      cache.setQueryData(['claim-detail', orgId, claim.id], claim);
      setSelectedId(claim.id);
      setEditing(false);
      setError('');
      toast.success('Claim saved');
    },
  });
  if (!can('read_claim')) return <NoPermission />;
  const run = (path: string, body: unknown, method: 'post' | 'put' = 'post') => mutation.mutate({ path, body, method });
  const saveBilling = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    const form = new FormData(event.currentTarget);
    const value = (key: string) => String(form.get(key) ?? '').trim();
    const party = (prefix: string): Party => ({
      name: value(`${prefix}_name`),
      city: value(`${prefix}_city`),
      address: value(`${prefix}_address`),
      zip_code: value(`${prefix}_zip_code`),
      state: value(`${prefix}_state`).toUpperCase(),
    });
    try {
      const encounterId = selected?.encounter_id || newEncounter;
      if (!encounterId) throw new Error('Choose an encounter.');
      const encounter =
        editSource ||
        (selected
          ? {
              id: selected.encounter_id,
              patient_id: selected.snapshot.encounter.patient_id,
              updated_at: selected.snapshot.encounter.source_version,
              encounter_date: selected.snapshot.encounter.lines[0].service_date,
            }
          : encounterOptions.find((e) => e.id === encounterId));
      if (!encounter) throw new Error('Load and review the encounter before saving.');
      if (selected && encounter.patient_id !== selected.snapshot.encounter.patient_id)
        throw new Error('The encounter patient changed. This claim cannot be reassigned.');
      if (!encounter.patient_id) throw new Error('Assign this encounter to a patient first.');
      const lineCount = Number(value('line_count'));
      const lines: Line[] = Array.from({ length: lineCount }, (_, i) => ({
        units: Number(value(`units_${i}`)),
        place_of_service: value(`pos_${i}`),
        paid_cents: cents(value(`paid_${i}`)),
        service_date: encounter.encounter_date,
        code: value(`code_${i}`).toUpperCase(),
        charge_cents: cents(value(`charge_${i}`)),
        diagnosis_pointers: value(`pointers_${i}`).split(',').map(Number),
        source_line_id: selected?.snapshot.encounter.lines[i]?.source_line_id || crypto.randomUUID(),
        modifiers: value(`modifiers_${i}`)
          .split(',')
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean),
      }));
      const patient = party('patient');
      const relationship = value('relationship');
      const billing: Billing = {
        patient,
        tax_id: value('tax_id'),
        provider: party('provider'),
        patient_dob: value('patient_dob'),
        coverage: {
          relationship,
          member_id: value('member_id'),
          payer_name: value('payer_name'),
          group_number: value('group_number'),
          effective_from: value('effective_from'),
          effective_to: value('effective_to') || null,
          subscriber: relationship === 'self' ? patient : party('subscriber'),
          subscriber_dob: relationship === 'self' ? value('patient_dob') : value('subscriber_dob'),
        },
        encounter: {
          lines,
          signed: true,
          encounter_id: encounter.id,
          rendering_npi: value('npi'),
          source_system: 'mohairnotes',
          patient_id: encounter.patient_id,
          policy_id: selected?.snapshot.encounter.policy_id || crypto.randomUUID(),
          diagnoses: value('diagnoses')
            .split(',')
            .map((s) => s.trim().toUpperCase()),
          source_version: editSource?.updated_at || selected?.snapshot.encounter.source_version || encounter.updated_at,
        },
      };
      run(
        selected ? `/${selected.id}` : '',
        selected ? { billing, expected_version: selected.version } : billing,
        selected ? 'put' : 'post',
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load encounter.');
    }
  };
  return (
    <div className="flex flex-col gap-6 p-4 md:p-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Out-of-network claims</h1>
          <p className="text-muted-foreground">Prepare billing records and track patient reimbursement.</p>
        </div>
        {can('manage_claim') && can('read_encounter') && (
          <Button
            onClick={() => {
              setSelectedId(null);
              setNewEncounter('');
              setEditSource(null);
              setEditing(true);
            }}>
            New billing record
          </Button>
        )}
      </header>
      {can('manage_claim') && (
        <a href="/eligibility" className="text-primary underline">
          Check eligibility & benefits
        </a>
      )}
      <Alert>
        <AlertTitle>Electronic filing is not connected</AlertTitle>
        <AlertDescription>
          Claims are not transmitted from this workspace. Record a submission only after the patient or practice has
          submitted it through a verified insurer channel.
        </AlertDescription>
      </Alert>
      {(error || claims.isError) && (
        <Alert variant="destructive">
          <AlertTitle>Action needs attention</AlertTitle>
          <AlertDescription>{error || 'Could not load claims.'}</AlertDescription>
          <Button variant="outline" onClick={() => claims.refetch()}>
            Reload claims
          </Button>
        </Alert>
      )}
      {editing ? (
        <>
          {selected && (
            <Alert>
              <AlertTitle>Review the encounter before updating billing</AlertTitle>
              <AlertDescription>
                Saving checks the source version you reviewed. If the encounter changed, load its current version and
                reconcile the billing fields before saving.
              </AlertDescription>
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  try {
                    const current = (
                      await clientApi.get<EncounterOut>(
                        `/api/v1/organizations/${orgId}/encounters/${selected.encounter_id}`,
                      )
                    ).data;
                    if (current.patient_id !== selected.snapshot.encounter.patient_id)
                      throw new Error('The encounter patient changed. This claim cannot be reassigned.');
                    setEditSource(current);
                    toast.success(
                      'Current encounter loaded. Review billing against the updated encounter before saving.',
                    );
                  } catch {
                    setError('Unable to reconcile this source. Check the encounter and patient assignment.');
                  }
                }}>
                Load current encounter for reconciliation
              </Button>
              {editSource && (
                <p>Loaded service date: {editSource.encounter_date}. Verify all services and charges before saving.</p>
              )}
            </Alert>
          )}
          {!selected && (
            <div className="flex gap-3 items-center">
              {encounters.hasNextPage && (
                <Button
                  variant="outline"
                  disabled={encounters.isFetchingNextPage}
                  onClick={() => encounters.fetchNextPage()}>
                  {encounters.isFetchingNextPage ? 'Loading…' : 'Load more encounters'}
                </Button>
              )}
              {encounters.isError && (
                <Button variant="outline" onClick={() => encounters.refetch()}>
                  Retry loading encounters
                </Button>
              )}
              <span>{encounterOptions.length} encounters available</span>
            </div>
          )}
          <BillingForm
            key={selected?.id || 'new'}
            busy={mutation.isPending}
            encounterId={newEncounter}
            billing={selected?.snapshot}
            encounters={encounterOptions}
            onSubmit={saveBilling}
            onCancel={() => setEditing(false)}
            setEncounterId={(id) => {
              setNewEncounter(id);
              setEditSource(encounterOptions.find((e) => e.id === id) || null);
            }}
          />
        </>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Claims</CardTitle>
              <CardDescription>
                {claims.isLoading ? 'Loading claims…' : 'Open a record to review billing and follow-up.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead>Service date</TableHead>
                    <TableHead>Insurer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Charges</TableHead>
                    <TableHead>Filing deadline</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {claims.data?.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <Button variant="link" onClick={() => setSelectedId(c.id)}>
                          {c.snapshot.patient.name}
                        </Button>
                      </TableCell>
                      <TableCell>{c.snapshot.encounter.lines[0].service_date}</TableCell>
                      <TableCell>{c.snapshot.coverage.payer_name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{c.status.replaceAll('_', ' ')}</Badge>
                      </TableCell>
                      <TableCell>
                        {dollars(c.snapshot.encounter.lines.reduce((n, l) => n + l.charge_cents, 0))}
                      </TableCell>
                      <TableCell>{c.review?.filing_deadline || 'Not verified'}</TableCell>
                    </TableRow>
                  ))}
                  {claims.data?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>No claims yet. Start with a billing record from an encounter.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <div className="mt-4 flex gap-2">
                <Button
                  variant="outline"
                  disabled={page === 0}
                  onClick={() => {
                    setPage(page - 1);
                    setSelectedId(null);
                  }}>
                  Previous
                </Button>
                <Button
                  variant="outline"
                  disabled={(claims.data?.length || 0) < 50}
                  onClick={() => {
                    setPage(page + 1);
                    setSelectedId(null);
                  }}>
                  Next
                </Button>
              </div>
            </CardContent>
          </Card>
          {selected && (
            <Card>
              <CardHeader>
                <CardTitle>{selected.snapshot.patient.name}</CardTitle>
                <CardDescription>
                  Version {selected.version} · {selected.snapshot.coverage.payer_name} ·{' '}
                  {selected.status.replaceAll('_', ' ')}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                {selected.source_changed && (
                  <Alert variant="destructive">
                    <AlertTitle>Source encounter changed</AlertTitle>
                    <AlertDescription>
                      {selected.status === 'draft'
                        ? 'Reconcile the billing record with the current encounter before approval.'
                        : 'This packet preserves the approved billing. Review the source change before using it for a new submission.'}
                    </AlertDescription>
                  </Alert>
                )}
                <p>
                  Patient paid: {dollars(selected.snapshot.encounter.lines.reduce((n, l) => n + l.paid_cents, 0))} ·
                  Diagnoses: {selected.snapshot.encounter.diagnoses.join(', ')}
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Modifiers</TableHead>
                      <TableHead>POS</TableHead>
                      <TableHead>Units</TableHead>
                      <TableHead>Charge</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selected.snapshot.encounter.lines.map((l) => (
                      <TableRow key={l.source_line_id}>
                        <TableCell>{l.code}</TableCell>
                        <TableCell>{l.modifiers.join(', ') || '—'}</TableCell>
                        <TableCell>{l.place_of_service}</TableCell>
                        <TableCell>{l.units}</TableCell>
                        <TableCell>{dollars(l.charge_cents)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {can('manage_claim') && selected.status === 'draft' && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditSource(null);
                        setEditing(true);
                      }}>
                      Edit billing
                    </Button>
                    <ActionForm kind="approve" claim={selected} busy={mutation.isPending} onSave={run} />
                  </>
                )}
                {selected.review && (
                  <div className="flex flex-col gap-3">
                    <h3 className="font-medium">Submission instructions</h3>
                    <p className="whitespace-pre-wrap">{selected.review.member_instructions}</p>
                    <SuperbillActions
                      key={selected.id}
                      base={`${base}/${selected.id}`}
                      canGenerate={can('manage_claim')}
                    />
                    <Button
                      variant="outline"
                      onClick={async () => {
                        try {
                          const response = await clientApi.get(`${base}/${selected.id}/packet`, {
                            responseType: 'blob',
                          });
                          const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = 'member-claim-packet.pdf';
                          link.click();
                          setTimeout(() => URL.revokeObjectURL(url), 1000);
                        } catch {
                          setError('Could not download the packet. Please reload and try again.');
                        }
                      }}>
                      Download member packet
                    </Button>
                    {can('manage_claim') && (
                      <>
                        <Button
                          variant="outline"
                          onClick={async () => {
                            try {
                              const result = await clientApi.post(`${base}/${selected.id}/patient-link`);
                              setPatientLink({
                                claimId: selected.id,
                                url: `${window.location.origin}/claim-portal#${result.data.token}`,
                              });
                            } catch {
                              setError('Could not create patient link.');
                            }
                          }}>
                          Create private patient link
                        </Button>
                        <p className="text-sm text-muted-foreground">
                          The link grants access to this claim for 48 hours. Deliver it to the verified patient through
                          your secure communication channel. Creating another link revokes the previous one.
                        </p>
                        <Button
                          variant="outline"
                          onClick={async () => {
                            try {
                              await clientApi.delete(`${base}/${selected.id}/patient-link`);
                              setPatientLink(null);
                              toast.success('Patient access revoked');
                            } catch {
                              setError('Could not revoke patient access.');
                            }
                          }}>
                          Revoke patient access
                        </Button>
                        {patientLink?.claimId === selected.id && (
                          <>
                            <Label htmlFor="patient-link">Private patient link</Label>
                            <Input
                              id="patient-link"
                              readOnly
                              value={patientLink.url}
                              onFocus={(e) => e.target.select()}
                            />
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
                {can('manage_claim') && selected.status === 'reviewed' && (
                  <ActionForm claim={selected} kind="submission" busy={mutation.isPending} onSave={run} />
                )}
                {can('manage_claim') && selected.status === 'submitted' && (
                  <ActionForm kind="eob" claim={selected} busy={mutation.isPending} onSave={run} />
                )}
                {selected.eob && (
                  <div>
                    <h3 className="font-medium">Reported EOB</h3>
                    <p>Payer claim: {selected.eob.payer_claim_number}</p>
                    <p>
                      Allowed: {dollars(selected.eob.allowed_cents)} · Paid to patient:{' '}
                      {dollars(selected.eob.paid_to_patient_cents)} · Applied to deductible:{' '}
                      {dollars(selected.eob.deductible_cents)}
                    </p>
                  </div>
                )}
                <div>
                  <h3 className="mb-2 font-medium">History</h3>
                  {history.isError ? (
                    <p>Could not load history.</p>
                  ) : (
                    history.data?.map((e, i) => (
                      <p key={i} className="text-sm text-muted-foreground">
                        {new Date(e.occurred_at).toLocaleString()} · {e.action.replaceAll('_', ' ')}
                      </p>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function Field({
  name,
  label,
  value = '',
  type = 'text',
  required = true,
}: {
  name: string;
  label: string;
  value?: string | number;
  type?: string;
  required?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={name}>{label}</Label>
      <Input
        id={name}
        name={name}
        type={type}
        required={required}
        defaultValue={value}
        step={type === 'number' ? '0.01' : undefined}
      />
    </div>
  );
}

function PartyFields({ title, party, prefix }: { prefix: string; title: string; party?: Party }) {
  return (
    <fieldset className="grid gap-4 sm:grid-cols-2">
      <legend className="mb-4 font-medium">{title}</legend>
      {(['name', 'address', 'city', 'state', 'zip_code'] as const).map((key) => (
        <Field
          key={key}
          value={party?.[key]}
          name={`${prefix}_${key}`}
          label={
            key === 'zip_code'
              ? 'ZIP code'
              : key === 'state'
                ? 'State (two letters)'
                : key.charAt(0).toUpperCase() + key.slice(1)
          }
        />
      ))}
    </fieldset>
  );
}
